import React, { useEffect } from "react"
import styled from "styled-components"
import PublicWebsiteHeader from "../components/PublicWebsiteHeader"
import Logo from "../components/Logo"
import Footer from "../components/Footer"
import { Helmet } from "react-helmet"
import * as Theme from "../libs/theme"
import { Element } from "react-scroll"
import analytics, { trackingEvents } from "../libs/analytics"

const steps = [
	{
		title: "Create and Send Your Lists",
		description:
			"Each list is assigned a unique URL to give to members of your organization.  Lists are public and only the admin needs to worry about usernames and password.",
		image: "create-list",
	},
	{
		title: "Users Submit Requests",
		description: "People add the groceries they would like provided to their list.",
		image: "add-item",
	},
	{
		title: "Collect and Shop Requests",
		description:
			"Requests are automatically (or manually) collected daily at a time of your choosing. Items are converted to a single sortable shopping list for you to give to a runner to use. The lists are then automatically cleared for the next day.",
		image: "shopping-list",
	},
] as const
const features = [
	{
		title: "Work Smarter, Not Harder",
		description:
			"Save time by not having to collect lists from each bus. Appear more respectable by giving your runners a digital shopping list.",
	},
	{
		title: "No Messy Usernames and Passwords",
		description:
			"Simply post the URL to each list in each bus. Users are not required to sign up for anything to add items to the list.",
	},
	{
		title: "Unlimited Lists",
		description: "Create as many separate lists as you want.",
	},
	{
		title: "Unlimited Items",
		description: "There is no limit to how many different grocery requests can be on each list.",
	},
	{
		title: "Bus Signage",
		description:
			"Print auto-generated signs for each bus with a unique QR code straight to their list's URL.",
	},
	{
		title: "Automatic vs Manual Processing",
		description:
			"Choose between daily processing at a time of your choosing, or manual collection whenever you decide.",
	},
	{
		title: "Recurring Items",
		description:
			"Constantly needing the same items? No problem - recurring items do not clear when the lists are processed and are automatically available for the next collection cycle.",
	},
	{
		title: "Always In Sync",
		description: "Everyone sees items added to the lists in real-time.",
	},
	{
		title: "Email Notifications",
		description:
			"You'll receive an email notification whenever the lists have been collected with a direct link for you to review and send to your runners.",
	},
	{
		title: "Supports Multiple Shoppers/Runners",
		description:
			"Sending multiple runners to divide and conquer? Shoppers can all view the list and check items off in real time.",
	},
] as const

const testimonials = [
	{
		quote: "Easy to use. Easy to understand. Easy to give runners. LOVE IT",
		name: "Daniel Martin",
		title: "Tour Manager",
		artist: "MercyMe",
	},
	{
		quote:
			"BusStock makes bus stock lists so much easier. The QR code makes it easy for anyone to add items. It organizes the lists for you to be able to just print off for the runner. Super helpful. It's a must-have.",
		name: "Melanie Hall",
		title: "Tour Manager",
		artist: "Casting Crowns",
	},
]

const PublicWebsiteIndex: React.FC = () => {
	useEffect(() => {
		analytics.track("Homepage", trackingEvents.HOMEPAGE_VIEWED)
	}, [])

	return (
		<FlexColumn>
			<Helmet>
				<title>Digitizing the Bus Stock Grocery Process</title>
			</Helmet>
			<PublicWebsiteHeader />
			<FeatureWrapper>
				<MobileOnly>
					<Logo size={40} textColor="#000" target="_self" />
				</MobileOnly>
				<picture>
					<source srcSet="/devices.webp" type="image/webp" />
					<DevicesImage src="/devices.png" alt="BusStock Devices - iPhone, iPad, Android" />
				</picture>
				<FeatureHeadline>
					<div>Collect Bus Stock Requests Digitally.</div>
					<CallToAction href="https://dashboard.busstock.app/">
						Start Your 7-day Free Trial
					</CallToAction>
					<NoStringsAttached>No Credit Card Required!</NoStringsAttached>
				</FeatureHeadline>
			</FeatureWrapper>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 1440 68"
				enableBackground="new 0 0 1440 68"
				style={{
					display: "block",
					margin: "-1px 0px 0px",
					padding: 0,
					transform: "rotate(180deg)",
				}}
			>
				<path
					d="m1622.3 1937.7c0 0-410.7 169.1-913.4 75.5-502.7-93.6-977.7 56.3-977.7 56.3v440h1891.1v-571.8"
					fill="#F4F5F7"
					transform="translate(0-1977)"
				/>
			</svg>
			<Tagline name="features">Stop using whiteboards and paper. There's an easier way!</Tagline>
			<StepsWrapper>
				{steps.map((step, index) => (
					<StepWrapper key={step.title}>
						<StepText>
							<StepTitle>
								Step {index + 1}: {step.title}
							</StepTitle>
							<StepDescription>{step.description}</StepDescription>
						</StepText>
						<StepImage>
							{/* <picture>
								<source
									srcSet={`${step.image}.webp`}
									type="image/webp"
								/> */}
							<div>
								<Screenshot src={`${step.image}.png`} alt={step.title} />
							</div>
							{/* </picture> */}
						</StepImage>
					</StepWrapper>
				))}
			</StepsWrapper>
			<Tagline name="more-features">More Features</Tagline>
			<FeaturesWrapper>
				{features.map((feature) => (
					<Feature key={feature.title}>
						<FeatureTitle>{feature.title}</FeatureTitle>
						{feature.description}
					</Feature>
				))}
			</FeaturesWrapper>
			<TestimonialsWrapper>
				{testimonials.map((testimonial) => (
					<Testimonial key={testimonial.name}>
						<TestimonialQuote>"{testimonial.quote}"</TestimonialQuote>
						<TestimonialName>{testimonial.name}</TestimonialName>
						<TestimonialTitle>{testimonial.title}</TestimonialTitle>
						<TestimonialArtist>{testimonial.artist}</TestimonialArtist>
					</Testimonial>
				))}
			</TestimonialsWrapper>
			<PricingWrapper>
				<PriceBox name="pricing">
					Weekly Pricing
					<Price>$3</Price>
					<PriceDescription>per bus</PriceDescription>
					<SignUpButton href="https://dashboard.busstock.app/?term=week">Sign Up</SignUpButton>
				</PriceBox>
				<PriceBox name="pricing">
					Monthly Pricing
					<Price>$10</Price>
					<PriceDescription>per bus</PriceDescription>
					<SignUpButton href="https://dashboard.busstock.app/?term=month">Sign Up</SignUpButton>
				</PriceBox>
				<PriceBox name="pricing">
					Yearly Pricing
					<Price>$100</Price>
					<PriceDescription>per bus</PriceDescription>
					<SignUpButton href="https://dashboard.busstock.app/?term=year">Sign Up</SignUpButton>
				</PriceBox>
			</PricingWrapper>
			<Footer />
		</FlexColumn>
	)
}
const FlexColumn = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	background: white;
	margin-top: 90px;
	@media only screen and (max-width: 767px) {
		margin-top: 0;
		margin-bottom: 90px;
	}
`
const FeatureWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 50px;
	background: rgb(244, 245, 247);
	@media only screen and (max-width: 1023px) {
		padding: 20px;
	}
	@media only screen and (max-width: 767px) {
		flex-direction: column;
	}
`
const MobileOnly = styled.div`
	display: none;
	@media screen and (max-width: 767px) {
		display: block;
	}
`
const FeatureHeadline = styled.h1`
	max-width: 400px;
	margin-left: 50px;
	font-weight: bold;
	font-size: 42px;
	@media only screen and (max-width: 1023px) {
		margin-left: 30px;
		font-size: 34px;
	}
	@media only screen and (max-width: 767px) {
		margin-left: 15px;
		font-size: 28px;
		max-width: 90%;
		text-align: center;
	}
`
const DevicesImage = styled.img`
	max-height: 500px;
	@media only screen and (max-width: 767px) {
		max-width: 80%;
		margin: 10px 0;
	}
`
const CallToAction = styled.a`
	font-size: 20px;
	color: #fff;
	margin-right: 0;
	padding: 10px 24px;
	background-color: ${Theme.Colors.Primary};
	border-radius: 5px;
	:hover {
		color: #fff;
		background-color: ${Theme.Colors.PrimaryHover};
	}
	@media only screen and (max-width: 767px) {
		display: inline-block;
		margin-top: 15px;
	}
`
const Tagline = styled(Element)`
	color: ${Theme.Colors.SecondaryText};
	font-weight: bold;
	font-size: 28px;
	margin-bottom: 40px;
	margin-top: 40px;
	text-align: center;
	padding: 0 20px;
`
const StepsWrapper = styled.section`
	text-align: center;
	padding: 20px;
`
const StepWrapper = styled.div`
	max-width: 1000px;
	width: 100%;
	padding: 60px 0;
	margin: 0 auto;
	text-align: left;
	display: flex;
	@media only screen and (max-width: 767px) {
		padding: 30px 0;
		flex-direction: column;
	}
`
const PricingWrapper = styled(StepsWrapper)`
	display: flex;
	justify-content: space-around;
	flex-wrap: wrap;
	max-width: 1000px;
	margin: 0 auto;
`
const StepText = styled.div`
	flex: 1;
`
const StepImage = styled.div`
	flex: 1;
	flex-basis: 50%;
	text-align: left;
	max-width: 50%;
	margin: auto;
	@media only screen and (max-width: 767px) {
		margin: 10px auto 0;
		max-width: 80%;
		text-align: center;
	}
`
const StepTitle = styled.div`
	font-size: 24px;
	margin-bottom: 20px;
	font-weight: bold;
`
const StepDescription = styled.div``
const FeaturesWrapper = styled.section`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	justify-content: center;
	margin: 0 auto;
	max-width: 1000px;
`
const Feature = styled.div`
	flex: 1;
	min-width: 50%;
	padding: 15px 30px;
	@media only screen and (max-width: 767px) {
		padding: 7px 15px;
		min-width: 80%;
	}
`
const FeatureTitle = styled.h3`
	margin-bottom: 10px;
`
const Screenshot = styled.img`
	max-width: 100%;
	box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.5);
`
const TestimonialsWrapper = styled.section`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
`
const Testimonial = styled.div`
	margin: 10px;
	padding: 20px;
	max-width: 400px;
	display: flex;
	flex-direction: column;
	color: white;
	border-radius: 20px;
	background-color: rgb(74, 74, 74);
	text-align: right;
`
const TestimonialQuote = styled.div`
	font-size: 22px;
	text-align: left;
	line-height: 1.5em;
	margin-bottom: 20px;
	flex: 1;
	font-weight: bold;
`
const TestimonialTitle = styled.div`
	font-style: italic;
`
const TestimonialName = styled.div`
	font-weight: bold;
`
const TestimonialArtist = styled.div`
	font-style: italic;
`

const PriceDescription = styled.div`
	font-size: 14px;
	color: ${Theme.Colors.SecondaryText};
	margin-bottom: 10px;
`
const SignUpButton = styled.a`
	color: #fff;
	margin-right: 0;
	padding: 10px 12px;
	background-color: ${Theme.Colors.Primary};
	border-radius: 5px;
	display: inline-block;
	:hover {
		color: #fff;
		background-color: ${Theme.Colors.PrimaryHover};
	}
`
const PriceBox = styled(Element)`
	min-width: 260px;
	background: rgb(244, 245, 247);
	flex: 1 1 0%;
	padding: 40px;
	text-align: center;
	margin: 20px;
	&:nth-child(2) {
		border-radius: 5px;
		margin: 0px;
		background: ${Theme.Colors.Primary};
		color: #fff;
		box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.5);

		${PriceDescription} {
			color: #fff;
		}
		${SignUpButton} {
			background-color: #fff;
			color: ${Theme.Colors.Primary};
			:hover {
				background-color: #f0f0f0;
			}
		}
	}
`
const Price = styled.div`
	font-size: 65px;
	font-weight: bold;
`

const NoStringsAttached = styled.div`
	font-size: 14px;
	color: ${Theme.Colors.SecondaryText};
	margin-top: 10px;
	font-style: italic;
`
export default PublicWebsiteIndex
